/**
 * CriticalPathPanel — the PM engine made visible.
 *
 * Runs the pure dependency engine (`crm/taskGraph.ts`) over the live task
 * snapshot and surfaces what was previously test-only logic as felt UI:
 * the ordered critical-path chain, a severity-ranked slip list (days late
 * / blocked reason), and a cycle warning when the graph is tangled.
 *
 * "Scan for fixes" runs the Project Sentinel (`agent/projectSentinel.ts`)
 * and routes every proposal through the EXISTING approvals write path
 * (`queueApproval`) — the same governed flow the Customer Success agent
 * uses — so fixes land in the Dashboard triage cockpit + Decision Ledger,
 * never as direct writes. De-dupes by proposed title against pending
 * approvals so re-scanning doesn't pile duplicates.
 *
 * Thin by design: all graph math lives in `crm/taskGraph.ts`, display
 * shaping in `crm/taskGraphView.ts`, proposal logic in the sentinel.
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronRight,
  GitBranch,
  RefreshCcw,
  ShieldCheck,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { useToast } from './ToastProvider';
import { C } from '../lib/tokens';
import { analyzeTaskGraph } from '../lib/crm/taskGraph';
import {
  formatSlips,
  orderedCriticalPath,
  type SlipSeverity,
} from '../lib/crm/taskGraphView';
import { proposeProjectFixes } from '../lib/agent/projectSentinel';
import { listApprovals, queueApproval } from '../lib/agent/approvals';
import type { CreateTaskAction } from '../lib/agent/types';
import type { ProjectTask } from '../lib/db/types';

/** Severity → pill class + label. Same pill vocabulary as the board. */
const SEVERITY_META: Record<SlipSeverity, { label: string; pill: string }> = {
  critical: { label: 'Critical', pill: 'glass-pill glass-pill--orange' },
  high: { label: 'High', pill: 'glass-pill glass-pill--magenta' },
  watch: { label: 'Watch', pill: 'glass-pill glass-pill--blue' },
};

/** Local today as YYYY-MM-DD, matching the graph engine's default. */
function localTodayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

interface CriticalPathPanelProps {
  /** Live task snapshot (already subscribed by the parent view). */
  tasks: ProjectTask[];
  /** Whether the operator may run the sentinel scan (staff+). */
  canScan: boolean;
}

export function CriticalPathPanel({ tasks, canScan }: CriticalPathPanelProps) {
  const { push } = useToast();
  const [scanning, setScanning] = useState(false);

  const today = localTodayIso();

  const analysis = useMemo(
    () => analyzeTaskGraph(tasks, today),
    [tasks, today],
  );
  const pathTasks = useMemo(
    () => orderedCriticalPath(tasks, analysis.criticalPath.ids),
    [tasks, analysis],
  );
  const slipRows = useMemo(
    () => formatSlips(tasks, analysis.slips, analysis.criticalPath.ids, today),
    [tasks, analysis, today],
  );

  // A "chain" of one task means no dependency edges shaped it — honest empty.
  const hasChain = pathTasks.length > 1;

  async function onScan() {
    setScanning(true);
    try {
      const proposals = proposeProjectFixes(tasks, today);
      if (proposals.length === 0) {
        push({ kind: 'info', text: 'Nothing to fix — the task graph is healthy.' });
        return;
      }
      // De-dupe by proposed title against pending approvals (same pattern
      // as the Customer Success agent) so re-scanning never piles dupes.
      const pending = await listApprovals('pending');
      const alreadyQueued = new Set(
        pending
          .filter((a) => a.action.kind === 'create_task')
          .map((a) => (a.action as CreateTaskAction).title),
      );
      let proposed = 0;
      let skipped = 0;
      for (const p of proposals) {
        const title =
          p.action.kind === 'create_task'
            ? (p.action as CreateTaskAction).title
            : null;
        if (title && alreadyQueued.has(title)) {
          skipped++;
          continue;
        }
        await queueApproval(p.action, 'scheduler', p.reason);
        if (title) alreadyQueued.add(title);
        proposed++;
      }
      if (proposed > 0) {
        push({
          kind: 'success',
          text: `Proposed ${proposed} fix${proposed === 1 ? '' : 'es'} — review in the inbox`,
          detail: skipped > 0 ? `${skipped} already awaiting approval` : undefined,
        });
      } else {
        push({
          kind: 'info',
          text: `All ${skipped} fix${skipped === 1 ? ' is' : 'es are'} already awaiting approval.`,
        });
      }
    } catch {
      push({ kind: 'error', text: 'Scan failed — try again.' });
    } finally {
      setScanning(false);
    }
  }

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
      {/* Head */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.20), rgba(187,65,152,0.10))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: C.magenta,
            }}
          >
            <GitBranch size={15} strokeWidth={2} />
          </span>
          <div>
            <h3 className="t-section-title" style={{ margin: 0 }}>
              Critical path &amp; slips
            </h3>
            <p className="t-meta" style={{ margin: '2px 0 0', color: C.textMuted }}>
              Dependency engine over the live board — longest chain, slipping tasks,
              governed fixes.
            </p>
          </div>
        </div>
        {canScan && (
          <button
            type="button"
            className="glass-btn glass-btn--primary"
            onClick={() => void onScan()}
            disabled={scanning}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <RefreshCcw size={13} />
            <span>{scanning ? 'Scanning…' : 'Scan for fixes'}</span>
          </button>
        )}
      </div>

      {/* Cycle warning — the graph flags cycles instead of crashing. */}
      {analysis.hasCycle && (
        <div
          className="t-meta"
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: C.orangeWarm,
          }}
        >
          <AlertTriangle size={13} />
          <span>
            Dependency cycle detected — {analysis.cycleIds.length} task
            {analysis.cycleIds.length === 1 ? '' : 's'} block each other. Run a scan to
            propose untangling it.
          </span>
        </div>
      )}

      {/* Critical path chain */}
      <div style={{ marginTop: 14 }}>
        <span className="t-eyebrow">Critical path</span>
        {hasChain ? (
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            {pathTasks.map((t, i) => (
              <span
                key={t.id}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {i > 0 && (
                  <ChevronRight size={12} color={C.textMuted} aria-hidden="true" />
                )}
                <span
                  className={
                    t.status === 'done'
                      ? 'glass-pill glass-pill--green'
                      : 'glass-pill glass-pill--magenta'
                  }
                >
                  {t.title}
                </span>
              </span>
            ))}
            <span className="t-meta" style={{ color: C.textMuted, marginLeft: 4 }}>
              {pathTasks.length} tasks deep
            </span>
          </div>
        ) : (
          <p className="t-meta" style={{ margin: '6px 0 0', color: C.textMuted }}>
            No dependency chain yet — link tasks with blockers to see the longest path
            through the work.
          </p>
        )}
      </div>

      {/* Slips */}
      <div style={{ marginTop: 14 }}>
        <span className="t-eyebrow">Slips</span>
        {slipRows.length === 0 ? (
          <p
            className="t-meta"
            style={{
              margin: '6px 0 0',
              color: C.textMuted,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <ShieldCheck size={13} color={C.green} />
            <span>No slips detected — every blocked task still has runway.</span>
          </p>
        ) : (
          <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
            {slipRows.map((row) => {
              const meta = SEVERITY_META[row.severity];
              return (
                <div
                  key={row.taskId}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.08)',
                    background: 'rgba(255,255,255,0.03)',
                  }}
                >
                  <span className={meta.pill} style={{ flex: '0 0 auto' }}>
                    {meta.label}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{row.title}</span>
                      {row.daysLate !== undefined && (
                        <span
                          className="t-meta"
                          style={{ color: C.orangeWarm, fontWeight: 700 }}
                        >
                          {row.daysLate} day{row.daysLate === 1 ? '' : 's'} late
                        </span>
                      )}
                      {row.onCriticalPath && (
                        <span className="glass-pill">Critical path</span>
                      )}
                    </div>
                    {row.details.map((d, i) => (
                      <p
                        key={i}
                        className="t-meta"
                        style={{ margin: '3px 0 0', color: C.textMuted }}
                      >
                        {d}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </GlassPanel>
  );
}
